import { PhotoClient } from '@michaeltoft/photoclient'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAddCatalogueItem } from '../basket/potentials'
import { MatchDetailDialog } from '../components/MatchDetailDialog'
import { useTranslation } from '../localization/useTranslation'
import { useCategoryOptions } from '../search/categories'
import { type CategoryFacet, type SearchProduct, searchByImage } from '../search/searchClient'
import { useSearchCurrency } from '../search/searchCurrency'
import { formatCategoryLabel } from '../util/category'
import { isDisplayableProductMetadata } from '../util/productMetadata'
import { useAddFlowStore } from '../store/addFlow'
import { useAuthStore } from '../store/auth'
import { downscaleImage } from '../upload/downscaleImage'
import { uploadPhoto } from '../upload/photoUpload'
import { Glyph, IconCamera, IconPlus, IconX } from '../ui/icons'
import { useToast } from '../ui/toast'
import { scrollWidgetToTop } from '../util/scroll'
import { useMoneyFmt } from '../util/useMoney'

type Stage = 'upload' | 'analyzing' | 'results' | 'failed'

/**
 * "Add by photo" visual search (mockup): upload a photo → the photo receiver returns a public
 * URL → `tradeinsearch2?image=<url>` returns ranked, trade-in-eligible matches (with a `score`)
 * plus category facets. The customer refines by category and inspects a match before confirming,
 * which adds it to the basket for real (matches carry `masterVariantId` + `ruleId`).
 */
export function PhotoFinder({ categoryOnly = false }: { categoryOnly?: boolean } = {}) {
  const config = useAuthStore((s) => s.config)
  const shopId = useAuthStore((s) => s.shopId)
  const token = useAuthStore((s) => s.token)
  const { add, adding } = useAddCatalogueItem()
  // Whether the "add by category" path is actually offerable (categories exist, not just the flag).
  // Also pre-warms the shared cache-first query so the picker opens instantly.
  const { hasCategories } = useCategoryOptions()
  const navigate = useNavigate()
  // Auth restores this tab's upload session before the flow mounts.
  const photoIdent = useAddFlowStore((s) => s.photoIdent)
  const photoUrl = useAddFlowStore((s) => s.photoUrl)
  const setPhotoUrl = useAddFlowStore((s) => s.setPhotoUrl)
  const receivePhotos = useAddFlowStore((s) => s.receivePhotos)
  const showToast = useToast((s) => s.show)
  const { t } = useTranslation()
  const fmt = useMoneyFmt()
  // Shop base currency, like the text search — display converts to the consumer's currency, and the
  // currency a match comes back priced in is what PotentialCreate is told to price the offer from.
  const searchCurrency = useSearchCurrency()

  const [stage, setStage] = useState<Stage>('upload')
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null)
  const [items, setItems] = useState<SearchProduct[]>([])
  const [facets, setFacets] = useState<CategoryFacet[]>([])
  const [cat, setCat] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [refining, setRefining] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const requestVersion = useRef(0)
  const addPending = useRef(false)

  const isCurrentPhoto = (url: string) => {
    const current = useAddFlowStore.getState()
    return current.photoIdent === photoIdent && current.photoUrl === url
  }

  const resetView = () => {
    requestVersion.current += 1
    setStage('upload')
    setCat(null)
    setItems([])
    setFacets([])
    setUploadedUrl(null)
    setError(null)
    setRefining(false)
  }

  const reset = () => {
    resetView()
    setPhotoUrl(null)
  }

  // The saved photo drives the view, including reloads, category returns and a successful add
  // finishing after navigation. Cleanup also prevents an older search from replacing newer state.
  useEffect(() => {
    if (photoUrl) {
      if (categoryOnly) navigate('/category')
      else void analyze(photoUrl)
    } else resetView()
    return () => { requestVersion.current += 1 }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoUrl, photoIdent, categoryOnly])

  // Run visual search for an already-hosted image URL. Shared by the direct device upload and the
  // desktop→phone QR handoff (both end up with a public image URL that tradeinsearch2 consumes).
  const analyze = async (url: string) => {
    if (!shopId || !isCurrentPhoto(url)) return
    const version = ++requestVersion.current
    setStage('analyzing')
    setError(null)
    setUploadedUrl(url)
    setCat(null)
    setRefining(false)
    try {
      const result = await searchByImage({
        shopId,
        imageUrl: url,
        language: config.locale,
        currency: searchCurrency,
      })
      if (version !== requestVersion.current || !isCurrentPhoto(url)) return
      setItems(result.items)
      setFacets(result.facets)
      setStage('results')
    } catch {
      if (version !== requestVersion.current || !isCurrentPhoto(url)) return
      // A failed search must never read as "no match" — that tells the customer their item isn't
      // wanted. The photo is already hosted, so the retry offered here re-runs just the search.
      setStage('failed')
    }
  }

  const onPhotos = (photos: { url: string }[]) => {
    if (!photoIdent) return
    receivePhotos(photoIdent, photos)
  }

  const onFile = async (file: File | undefined) => {
    if (!file || !shopId || !photoIdent) return
    const version = ++requestVersion.current
    const isCurrent = () => version === requestVersion.current &&
      useAddFlowStore.getState().photoIdent === photoIdent
    setStage('analyzing')
    setError(null)
    try {
      const image = await downscaleImage(file)
      if (!isCurrent()) return
      const url = await uploadPhoto(image)
      if (!isCurrent()) return
      onPhotos([{ url }])
    } catch (e) {
      if (!isCurrent()) return
      setError(e instanceof Error ? e.message : t('photo.uploadFailed'))
      setStage('upload')
    }
  }

  // Refine the current image results to one category (or clear it). Re-queries the server, since the
  // facet count spans the whole match set, not just the loaded page — so filtering the loaded items
  // would only ever show the same handful. Stays on the results view (a light dim) and keeps facets.
  const refine = async (categoryId: string | null) => {
    if (!shopId || !uploadedUrl || refining) return
    const version = ++requestVersion.current
    const isCurrent = () => version === requestVersion.current && isCurrentPhoto(uploadedUrl)
    const previousCat = cat
    setCat(categoryId)
    setRefining(true)
    // The chips sit below the results — jump back to the top so the refined set is in view (no-op if
    // the customer is already at/above the top; see scrollWidgetToTop).
    scrollWidgetToTop()
    try {
      const result = await searchByImage({
        shopId,
        imageUrl: uploadedUrl,
        language: config.locale,
        currency: searchCurrency,
        categoryId: categoryId ?? undefined,
      })
      if (!isCurrent()) return
      setItems(result.items)
    } catch {
      if (!isCurrent()) return
      // Put the chip back and keep the matches already on screen: a failed refine must not look
      // like "this category is empty". The customer keeps what they had, plus an honest nudge.
      setCat(previousCat)
      showToast(t('finder.searchFailed'))
    } finally {
      if (isCurrent()) setRefining(false)
    }
  }

  const pickFile = () => fileRef.current?.click()

  // Desktop only: you can't scan a QR shown on the same phone. Detect "desktop" via pointer/hover
  // media features — a mouse-like fine pointer + hover, which phones and tablets don't have. This is
  // more reliable than touch-capability checks (some phones report maxTouchPoints 0; some laptops
  // report touch). Not viewport width, since the widget is embedded in a narrow container.
  const isDesktop =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  const showQr = Boolean(token) && isDesktop

  // Keep the same upload identity when the customer clears/adds a photo. The phone stays paired;
  // our persisted acknowledgements prevent remounts/polling from replaying dismissed uploads.
  const qrClient =
    showQr && token && photoIdent ? (
      <PhotoClient
        key={photoIdent}
        receiverId="tradein"
        accessToken={token}
        photoIdent={photoIdent}
        size={112}
        onImageUploaded={onPhotos}
      />
    ) : null

  const confirm = async (product: SearchProduct) => {
    if (addPending.current) return
    addPending.current = true
    const photo = uploadedUrl
    try {
      await add(product)
      if (photo && isCurrentPhoto(photo)) setPhotoUrl(null)
      showToast(t('finder.added', { name: product.name, amount: fmt(product.tradeInValue) }))
    } catch {
      showToast(t('photo.uploadFailed'))
    } finally {
      addPending.current = false
    }
  }

  // No `capture` attribute: that would force the camera on mobile. Omitting it lets the OS offer the
  // full picker — take a photo OR choose an existing image from the library.
  const hiddenInput = (
    <input
      ref={fileRef}
      type="file"
      accept="image/*"
      hidden
      onChange={(e) => onFile(e.target.files?.[0])}
    />
  )

  if (stage === 'upload') {
    return (
      <>
        {hiddenInput}
        {/* Two equal ways to provide the one photo (this device / your phone), joined by an "or". The
            heading/subtitle live once in the finder header (method-aware), not repeated here. */}
        <div className="photo-ways">
          <button className="way way--action" type="button" onClick={pickFile}>
            <IconCamera />
            <b>{t('photo.wayDevice')}</b>
          </button>
          {qrClient && (
            <>
              <div className="photo-or">{t('photo.or')}</div>
              <div className="way">
                <div className="qr-code">{qrClient}</div>
                <b>{t('photo.wayPhone')}</b>
              </div>
            </>
          )}
        </div>
        {error && (
          <p className="res-note" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        )}
      </>
    )
  }

  if (stage === 'analyzing') {
    // Same spinner, honest copy: categories-only just uploads the photo (no catalogue comparison).
    return (
      <div className="analyzing">
        <span className="spin" aria-hidden />
        <b>{t(categoryOnly ? 'photo.uploadingTitle' : 'photo.analysingTitle')}</b>
        <small>{t(categoryOnly ? 'photo.uploadingHint' : 'photo.analysingHint')}</small>
      </div>
    )
  }

  // The analysed photo, with the "replace" affordance. Shared by the results and failed views.
  const uploadedHeader = (
    <div className="uploaded">
      <span className="uthumb">
        {uploadedUrl ? <img src={uploadedUrl} alt={t('photo.yourPhoto')} /> : <IconCamera />}
      </span>
      <span className="utext">
        <b>{t('photo.yourPhoto')}</b>
        <small>{t(stage === 'failed' ? 'photo.notSearched' : 'photo.matchedTo')}</small>
      </span>
      <button
        type="button"
        className="uploaded-x"
        onClick={reset}
        disabled={adding}
        aria-label={t('photo.replace')}
      >
        <IconX />
      </button>
    </div>
  )

  // The search itself failed (masterdata down, embedding failure, ...). Deliberately distinct from
  // "no match": the photo is kept and the customer chooses when to retry, rather than the app
  // hammering a service that is already unhealthy.
  if (stage === 'failed') {
    return (
      <>
        {hiddenInput}
        {uploadedHeader}
        <div className="photo-h">{t('photo.searchFailedHeading')}</div>
        <p className="res-note">{t('photo.searchFailedBody')}</p>
        <div className="photo-retry">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => uploadedUrl && void analyze(uploadedUrl)}
          >
            {t('photo.tryAgain')}
          </button>
        </div>
      </>
    )
  }

  // results — ranked by score. The score scale is unspecified (not 0..1), so "best/strong"
  // is derived rank-relative rather than from an absolute threshold.
  const sorted = [...items].sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
  const topScore = sorted[0]?.score ?? 0
  const isStrong = (m: SearchProduct) => topScore > 0 && (m.score ?? 0) >= 0.9 * topScore
  // Base view: the top few confident matches. In a category: the category's matches (server-filtered
  // via re-query), so show more than the confident top-6 — capped to keep the grid manageable.
  const list = (cat ? sorted.filter((m) => m.category?.id === cat) : sorted).slice(0, cat ? 24 : 6)
  const activeCatRaw = facets.find((f) => f.id === cat)?.name ?? cat
  const activeCatName = activeCatRaw ? formatCategoryLabel(activeCatRaw) : ''
  const heading =
    items.length === 0
      ? t('photo.noMatchHeading')
      : cat
        ? t('photo.headingCategory', { category: activeCatName ?? '' })
        : t('photo.headingConfident')

  // The "can't find it? pick a category" fallback tile — mirrors the "Add manually" card in the
  // order-feed grid. Only when the shop actually has categories (not merely `wholeSaleActive`). Sits
  // as the last tile in the match grid; when there are no matches it's the whole grid.
  const categoryCard = hasCategories ? (
    <button
      type="button"
      className="card-add"
      disabled={adding}
      onClick={() => {
        // The current photo is already saved; go to the category route.
        navigate('/category')
      }}
    >
      <IconPlus />
      <b>{t('photo.categoryCardTitle')}</b>
      <small>{t('photo.categoryCardHint')}</small>
    </button>
  ) : null

  return (
    <>
      {hiddenInput}
      {uploadedHeader}

      <div className="photo-h">{heading}</div>

      {items.length === 0 ? (
        <>
          <p className="res-note">{t('photo.noMatchBody')}</p>
          {categoryCard && <div className="grid match-grid">{categoryCard}</div>}
        </>
      ) : (
        <>
          <div
            className="grid match-grid"
            style={refining ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
          >
            {list.map((m, i) => (
              <MatchDetailDialog
                key={m.masterVariantId}
                product={m}
                defaultImage={config.defaultImage}
                strong={isStrong(m)}
                onConfirm={() => confirm(m)}
                trigger={
                  <button type="button" className="card match" disabled={adding}>
                    {!cat && i === 0 && <span className="best-badge">{t('photo.bestMatch')}</span>}
                    <div className="pic">
                      {m.image || config.defaultImage ? (
                        <img src={m.image || config.defaultImage} alt="" />
                      ) : (
                        <Glyph name="shirt" />
                      )}
                    </div>
                    <div className="cbody">
                      <div className="nm">{m.name}</div>
                      <div className="sub">
                        {m.category?.name && m.category.name.toLowerCase() !== 'unknown'
                          ? formatCategoryLabel(m.category.name)
                          : isDisplayableProductMetadata(m.color)
                            ? m.color
                            : ''}
                      </div>
                    </div>
                  </button>
                }
              />
            ))}
            {/* Last tile: the category fallback, so "none of these fit" is right where the eye ends. */}
            {categoryCard}
          </div>

          {facets.length > 0 && (
            <div className="refine-block">
              <div className="refine-label">{t('photo.refine')}</div>
              <div className="cats">
                {cat && (
                  <button
                    className="chip clear"
                    type="button"
                    disabled={refining}
                    onClick={() => refine(null)}
                  >
                    {t('photo.clear')}
                  </button>
                )}
                {facets.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className="chip"
                    aria-pressed={cat === f.id}
                    disabled={refining}
                    onClick={() => refine(cat === f.id ? null : f.id)}
                  >
                    {formatCategoryLabel(f.name)} <span className="ccount">{f.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Hidden listener keeps the phone handoff live here — a new phone photo re-runs the search —
          without showing the QR panel again. Mounted (display:none) so PhotoClient keeps polling. */}
      {qrClient && (
        <div className="qr-listener" aria-hidden>
          {qrClient}
        </div>
      )}
    </>
  )
}
